import { useContext } from 'react'

import type { Draft, DraftChannel } from 'types/graphql'

import { DayContext } from 'src/lib/dayContext'

import {
  extensionsEmailDraft,
  extensionsSlackDraft,
} from '../Tiptap/extensions'
import { editorStyle } from '../Tiptap/styles'

// modified version of editorStyle with zero line height and padding to match
// email editor styles
export const compactEditorStyle = {
  ...editorStyle,
  pb: 0,
  // Override the vertical rhythm spacing
  '& .tiptap': {
    ...editorStyle['& .tiptap'],
    // Remove vertical spacing between elements
    '& > * + *': {
      ...editorStyle['& .tiptap']['& > * + *'],
      marginTop: '0',
    },

    // Override paragraph styles to remove line height
    '& p': {
      ...editorStyle['& .tiptap']['& p'],
      lineHeight: '0',
      marginBottom: '0',
    },

    // Override heading styles
    '& h1': {
      ...editorStyle['& .tiptap']['& h1'],
      lineHeight: '0',
      marginTop: '0',
      marginBottom: '0',
    },

    '& h2': {
      ...editorStyle['& .tiptap']['& h2'],
      lineHeight: '0',
      marginTop: '0',
      marginBottom: '0',
    },

    '& h3': {
      ...editorStyle['& .tiptap']['& h3'],
      lineHeight: '0',
      marginTop: '0',
      marginBottom: '0',
    },

    '& h4': {
      ...editorStyle['& .tiptap']['& h4'],
      lineHeight: '0',
      marginTop: '0',
      marginBottom: '0',
    },

    // Override list styles
    '& ul, & ol': {
      ...editorStyle['& .tiptap']['& ul, & ol'],
      marginTop: '13px',
      marginBottom: '13px',

      '& li': {
        ...editorStyle['& .tiptap']['& ul, & ol']['& li'],
        marginBottom: '0',

        '& p': {
          ...editorStyle['& .tiptap']['& ul, & ol']['& li']['& p'],
          margin: '0',
          padding: '0',
          lineHeight: '0',
        },
      },
    },

    // Override blockquote styles
    '& blockquote': {},

    '& div': {
      ...editorStyle['& .tiptap']['& p'],
      marginBottom: '0',
      '&:not(:last-child)': {
        marginBottom: 0,
      },
    },

    // Override table styles
    '& table': {
      ...editorStyle['& .tiptap']['& table'],
      margin: '0',
    },

    // Override pre/code styles
    '& pre': {
      ...editorStyle['& .tiptap']['& pre'],
      margin: '0',
      padding: '4px',
    },
  },
}

export const editorWrapperSx = {
  ...compactEditorStyle,
  height: '200px',
  pb: 0,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
}

export const editorContentSx = {
  flex: '1 1 auto',
  display: 'flex',
  pb: 0,
  flexDirection: 'column',
  '& .ProseMirror': {
    flex: '1 1 auto',
    minHeight: '200px',
    height: 'auto',
    overflowY: 'auto',
    width: '100%',
    padding: 2,
    // Additional overrides for ProseMirror content
    '& p, & h1, & h2, & h3, & h4, & li': {
      margin: '0 !important',
      padding: '0 !important',
    },
    '& ul, & ol': {
      margin: '0 !important',
      padding: '0 0 0 16px !important',
    },
    '& blockquote': {
      margin: '0 !important',
      padding: '4px !important',
    },
    '& table': {
      margin: '0 !important',
    },
    '& pre': {
      margin: '0 !important',
      padding: '4px !important',
    },
  },
}

export const getExtensions = (draftChannel: DraftChannel) => {
  switch (draftChannel) {
    case 'EMAIL':
      return [...extensionsEmailDraft]
    case 'SLACK':
      return [...extensionsSlackDraft]
  }
}

export const draftChannelLabel = {
  EMAIL: 'Email',
  SLACK: 'Slack',
}

export const useValidatedFromAddress = (draft: Draft) => {
  const { workAccounts } = useContext(DayContext)

  const workAccountsWithGmailStatus = workAccounts.filter((workAccount) =>
    workAccount.googleSyncStatuses.find(
      (status) => status.type === 'GMAIL' && status.status !== 'SUSPENDED'
    )
  )

  const validatedFromAddress = workAccountsWithGmailStatus.find(
    (workAccount) => workAccount.email === draft.email.from
  )?.email

  return {
    validatedFromAddress,
    workAccountsWithGmailStatus,
  }
}
